// SocialPulse OIDC helpers — public-client PKCE flow.
// The frontend never sees SocialPulse tokens; it only obtains the authorization
// `code` + hands it to our backend, which does the server-to-server exchange.
//
// CRA env vars only (no Vite):
//   REACT_APP_SOCIALPULSE_OIDC_ISSUER    e.g. "https://socialpulse.com/api"
//   REACT_APP_SOCIALPULSE_OIDC_CLIENT_ID e.g. "naturepulse-web"

const ISSUER = process.env.REACT_APP_SOCIALPULSE_OIDC_ISSUER || "";
// Site-aware client_id — the FarmersPulse fork registers under its own
// OIDC client. IS_FARMERSPULSE is set by lib/site.js from the hostname
// (`farmerspulse-*` / farmerspulse.com) or REACT_APP_SITE=farmerspulse.
import { IS_FARMERSPULSE } from "@/lib/site";
const CLIENT_ID = IS_FARMERSPULSE
  ? (process.env.REACT_APP_SOCIALPULSE_OIDC_CLIENT_ID_FARMERSPULSE || "farmerspulse-web")
  : (process.env.REACT_APP_SOCIALPULSE_OIDC_CLIENT_ID || "naturepulse-web");
const STORAGE_KEY = "np:oidc:pkce";

export const socialPulseEnabled = () => !!ISSUER;

const b64u = (buf) =>
  btoa(String.fromCharCode(...new Uint8Array(buf)))
    .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
const rand = () => b64u(crypto.getRandomValues(new Uint8Array(32)).buffer);

// Callback URL — canonical is `/auth/callback` per the ecosystem SSO
// integration guide (`/app/docs/sister-site-sso-integration.md` §4).
// The legacy `/auth/socialpulse-callback` route is retained in App.js
// for backwards compatibility with any IdP registration that still
// points at the old URL.
const CALLBACK_PATH = "/auth/callback";

export async function startSocialPulseLogin({ returnTo = "/" } = {}) {
  if (!ISSUER) throw new Error("SocialPulse SSO not configured");
  const verifier = rand();
  const state = rand();
  const nonce = rand();
  const challenge = b64u(
    await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier))
  );
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ verifier, state, nonce, returnTo }));
  const q = new URLSearchParams({
    client_id: CLIENT_ID,
    response_type: "code",
    redirect_uri: `${window.location.origin}${CALLBACK_PATH}`,
    scope: "openid profile email",
    state,
    nonce,
    code_challenge: challenge,
    code_challenge_method: "S256",
  });
  window.location.assign(`${ISSUER}/oauth/authorize?${q.toString()}`);
}

// Called by the callback page. `http` is our axios instance with baseURL=`${BACKEND}/api`.
export async function finishSocialPulseLogin(http) {
  const p = new URLSearchParams(window.location.search);
  const err = p.get("error");
  if (err) throw new Error(p.get("error_description") || err);
  const code = p.get("code");
  const state = p.get("state");
  if (!code || !state) throw new Error("missing code or state");
  const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "null");
  if (!saved || saved.state !== state) throw new Error("state mismatch (CSRF)");

  const { data } = await http.post("/auth/socialpulse/session", {
    code,
    code_verifier: saved.verifier,
    nonce: saved.nonce,
    // Send the *current* pathname so the backend can accept either the
    // new `/auth/callback` or the legacy `/auth/socialpulse-callback`
    // redirect_uri without a redeploy.
    redirect_uri: `${window.location.origin}${window.location.pathname}`,
  });
  sessionStorage.removeItem(STORAGE_KEY);
  return { ...data, returnTo: saved.returnTo || "/" };
}
